import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, Heading, Spacer, Text, Textarea} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox, CustomIntRadioGroup} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';

const PetCremationMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);

  const [title, setTitle] = useTitle('Pet Cremation Survey Maintenance');

  const isMounted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      setData(data);
      reset(data);
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('Pet Cremation Survey Maintenance');
    assignIds();
  };

  // ids for the good/excellent/na questions for the buttons to auto assign
  const assignIds = () => {
    let arr = [];
    const prompts = [prompt3, prompt4, prompt5, prompt2];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_PET_CREMATION;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_PET_CREMATION + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  // The ids must correspond to the data that is sent from api, otherwise defaults and sending data up won't work
  const prompt2 = [
    {prompt: '2a. Care & Concern', id: 'sv2a'},
    {prompt: 'b. Options & Prices', id: 'sv2b'},
  ];

  const prompt3 = [
    {prompt: '3a. Transfer', id: 'sv3a'},
    {prompt: 'b. Meet Your Needs', id: 'sv3b'},
    {prompt: 'c. Knowledgeable', id: 'sv3c'},
    {prompt: 'd. Appearance of Staff', id: 'sv3d'},
    {prompt: 'e. Honest & Sincere', id: 'sv3e'},
    {prompt: 'f. Return of Pet', id: 'sv3f'},
  ];

  const prompt4 = [{prompt: '4a. Value', id: 'sv4a'}];
  const prompt5 = [{prompt: '5a. Satisfaction', id: 'sv5a'}];
  const list = [
    {key: 'Yes', value: 1},
    {key: 'No', value: 2},
    {key: 'N/A', value: 3},
  ];

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              isPet
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Heading size="sm" mr="4">
                      1.
                    </Heading>
                    <Box textAlign="start">
                      <CustomCheckbox name="Previous Experience" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Convenience" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                      <CustomCheckbox name="Website" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                      <CustomCheckbox name="Advertising" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Vet Recommendation" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                      <CustomCheckbox name="Family/Friend Rec" id={'sv1f'} control={control} defaultChecked={data.sv1f} />
                      <CustomCheckbox name="Price" id={'sv1g'} control={control} defaultChecked={data.sv1g} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1dd" name="Advertising" {...register('sv1dd')} errors={errors} />
                  <CustomCheckbox name="Other " id={'sv1h'} control={control} defaultChecked={data.sv1h} ml="7" />
                  <Textarea rows={1} id="sv1hd" name="Other" {...register('sv1hd')} errors={errors} />
                </BoxCard>
                <SatisfactionSection control={control} prompts={prompt2} data={data} />
                <SatisfactionSection control={control} prompts={prompt3} data={data} />
              </Box>
              <Box w="40%">
                <SatisfactionSection control={control} prompts={prompt4} data={data} />
                <SatisfactionSection control={control} prompts={prompt5} data={data} />
                <SatisfactionToTen control={control} question={{prompt: '6a. Recommendation', id: 'sv6a'}} data={data} />

                <BoxCard textAlign="start">
                  <Heading size="sm">7a. Interested in Learning More</Heading>
                  <CustomIntRadioGroup control={control} id="sv7a" errors={errors} list={list} defaultValue={data.sv7a} />
                  <CustomCheckbox name="Grief Support" id={'sv7a1'} control={control} defaultChecked={data.sv7a1} />
                  <CustomCheckbox name="Pet Memorialization Merchandise" id={'sv7a2'} control={control} defaultChecked={data.sv7a2} />
                  <CustomCheckbox name="Animal Health Needs and Supplies" id={'sv7a4'} control={control} defaultChecked={data.sv7a4} />
                  <CustomCheckbox name="Pet Adoption Services" id={'sv7a5'} control={control} defaultChecked={data.sv7a5} />
                  <CustomCheckbox name="Pre-Arranging With Funeral Home" id={'sv7a3'} control={control} defaultChecked={data.sv7a3} />
                </BoxCard>
              </Box>

              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: false, surveyFormat: false, recommendations: false}}
                  data={data}
                  register={register}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  isSaving={isSaving}
                />
              </Box>
            </Flex>
            <FlexCard>
              <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} />
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default PetCremationMaintenance;
